import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  FlightStatusForm,
  FlightStatusFormData
} from '../../models/interfaces/flight-status-form.interface';
import { SelectedAirport } from '../models/validation.interfaces';
import { FlightStatusAirportService } from '../services/flight-status-airport.service';
import { FlightStatusService } from '../services/flight-status.service';

/**
 * Flight Status Form Handler
 * Handles form initialization, input processing, and data emission
 */
@Injectable()
export class FlightStatusFormHandler {
  constructor(
    private fb: FormBuilder,
    private datePickerService: FlightStatusService,
    private airportService: FlightStatusAirportService
  ) {}

  createForm(): FormGroup {
    const today = new Date();
    const todayFormatted = this.datePickerService.formatDateForForm(today);

    return this.fb.group({
      flightNumber: ['', [Validators.pattern(/^[0-9]*$/)]],
      departureDate: [todayFormatted]
    });
  }

  getInitialDateText(): string {
    const today = new Date();
    return this.datePickerService.formatDateForDisplay(today);
  }

  processFlightNumberInput(event: Event, form: FormGroup): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/\D/g, '').slice(0, 9);
    form.patchValue({ flightNumber: input.value });

    // Clear airport fields when user enters flight number
    if (input.value.length > 0) {
      this.airportService.clearAll();
    }
  }

  handleAirportChange(airport: SelectedAirport, form?: FormGroup): void {
    if (!airport?.code) return;

    setTimeout(() => {
      if (this.airportService.airportCodeInstance && airport.code) {
        this.airportService.setSelectedAirport(airport.code);

        // Clear flight number when user selects airport
        if (form) {
          form.patchValue({ flightNumber: '' });
        }
      }
    }, 100);
  }

  createFormData(form: FormGroup): FlightStatusFormData {
    const airportSelection = this.airportService.getSelection();

    return {
      flightNumber: form.value.flightNumber,
      departureDate: form.value.departureDate,
      origin: airportSelection.origin,
      destination: airportSelection.destination
    };
  }

  getFlightNumber(form: FormGroup): string {
    return form.value.flightNumber?.trim() || '';
  }

  getDeparture(form: FormGroup): string {
    return form.value.departureDate?.trim() || '';
  }

  initializeAirportCode(
    onModalOpen: () => void,
    content?: FlightStatusForm
  ): void {
    this.airportService.initializeAirportCode(
      () => onModalOpen(),
      () => onModalOpen(),
      content
    );
  }

  destroy(): void {
    this.datePickerService.destroy();
    this.airportService.destroy();
  }
}
